import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import { applyFontPairing } from "./fonts";
import { applyBrandTheme, clearBrandTheme } from "./brandTheme";
import { useTheme } from "./theme";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const { theme, applyTheme } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setSettings(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Apply chosen font pairing to the document whenever settings change.
  useEffect(() => {
    if (settings?.font_pairing) applyFontPairing(settings.font_pairing);
  }, [settings?.font_pairing]);

  // White-label accent: paint the workspace's colours over the brand tokens.
  useEffect(() => {
    if (!settings) { clearBrandTheme(); return; }
    applyBrandTheme({
      accent: settings.primary_color,
      surface: settings.secondary_color,
      dark: theme === "dark",
    });
  }, [settings, settings?.primary_color, settings?.secondary_color, theme]);

  // Workspace default theme applies only until the person picks their own.
  useEffect(() => {
    const chosen = localStorage.getItem("studio_theme");
    const preferred = settings?.default_theme;
    if (!chosen && (preferred === "light" || preferred === "dark")) applyTheme(preferred);
  }, [settings?.default_theme, applyTheme]);

  const update = async (patch) => {
    const { data } = await api.put("/settings", patch);
    setSettings(data);
    return data;
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, update, refresh: load }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);

// terminology helper
export const useLabel = () => {
  const { settings } = useSettings() || {};
  return (key, fallback) => (settings?.module_labels?.[key] || fallback);
};
